/**
 * Usage analytics (Google Analytics 4, first-party product counts) — the regional default and the resulting state.
 *
 * - "ask": prior opt-in consent is required where the person is (ASK_FIRST_COUNTRIES), or the country is unknown.
 *   The app shows the "Usage analytics" prompt and collects nothing until "Allow".
 * - "on": elsewhere. Collection starts without a prompt; Settings says "On (you can turn this off)".
 * - "off": the browser sends Global Privacy Control or Do Not Track — honoured everywhere (California's CCPA/CPRA
 *   treats GPC as a valid opt-out of sale/sharing; we apply it as a full opt-out).
 * - The person's explicit choice (allow / don't allow) always wins over the default and is never overridden.
 *
 * The country is the request country Cloudflare reports for GET /v1/auth/methods (`country`, ISO 3166-1 alpha-2);
 * every client (web, desktop, iOS, Android) asks the same endpoint. iOS has no GPC signal: country only.
 */

export type AnalyticsDefault = "ask" | "on" | "off";
/** A stored, explicit answer (prompt or settings switch). Absent = never answered. */
export type AnalyticsChoice = "granted" | "denied";

/** Browser privacy signals (`navigator.globalPrivacyControl`, `navigator.doNotTrack === "1"`); native apps pass none. */
export type AnalyticsSignals = { globalPrivacyControl?: boolean; doNotTrack?: boolean };

/**
 * Where analytics needs prior opt-in consent, with the legal basis. Edit here (the native ports mirror this list and
 * the golden vectors pin it). Keys are ISO 3166-1 alpha-2, as Cloudflare's `request.cf.country` reports them.
 */
export const ASK_FIRST_COUNTRIES: Readonly<Record<string, string>> = {
  // EU member states (27): ePrivacy Directive 2002/58/EC art. 5(3) (consent before non-essential cookies/storage) + GDPR.
  AT: "EU — ePrivacy art. 5(3), GDPR",
  BE: "EU — ePrivacy art. 5(3), GDPR",
  BG: "EU — ePrivacy art. 5(3), GDPR",
  HR: "EU — ePrivacy art. 5(3), GDPR",
  CY: "EU — ePrivacy art. 5(3), GDPR",
  CZ: "EU — ePrivacy art. 5(3), GDPR",
  DK: "EU — ePrivacy art. 5(3), GDPR",
  EE: "EU — ePrivacy art. 5(3), GDPR",
  FI: "EU — ePrivacy art. 5(3), GDPR",
  FR: "EU — ePrivacy art. 5(3), GDPR (CNIL cookie guidelines)",
  DE: "EU — ePrivacy art. 5(3), GDPR (TDDDG §25)",
  GR: "EU — ePrivacy art. 5(3), GDPR",
  HU: "EU — ePrivacy art. 5(3), GDPR",
  IE: "EU — ePrivacy art. 5(3), GDPR",
  IT: "EU — ePrivacy art. 5(3), GDPR (Garante cookie guidelines)",
  LV: "EU — ePrivacy art. 5(3), GDPR",
  LT: "EU — ePrivacy art. 5(3), GDPR",
  LU: "EU — ePrivacy art. 5(3), GDPR",
  MT: "EU — ePrivacy art. 5(3), GDPR",
  NL: "EU — ePrivacy art. 5(3), GDPR (Telecommunicatiewet 11.7a)",
  PL: "EU — ePrivacy art. 5(3), GDPR",
  PT: "EU — ePrivacy art. 5(3), GDPR",
  RO: "EU — ePrivacy art. 5(3), GDPR",
  SK: "EU — ePrivacy art. 5(3), GDPR",
  SI: "EU — ePrivacy art. 5(3), GDPR",
  ES: "EU — ePrivacy art. 5(3), GDPR (LSSI art. 22.2)",
  SE: "EU — ePrivacy art. 5(3), GDPR",
  // EEA, outside the EU (3): ePrivacy and GDPR apply through the EEA Agreement.
  IS: "EEA — ePrivacy, GDPR via the EEA Agreement",
  LI: "EEA — ePrivacy, GDPR via the EEA Agreement",
  NO: "EEA — ePrivacy, GDPR via the EEA Agreement (ekomloven §2-7b)",
  // EU territory that Cloudflare reports under its own code (outermost regions, Åland): EU law applies.
  AX: "EU (Finland, Åland) — ePrivacy, GDPR",
  GF: "EU (France, outermost region) — ePrivacy, GDPR",
  GP: "EU (France, outermost region) — ePrivacy, GDPR",
  MQ: "EU (France, outermost region) — ePrivacy, GDPR",
  RE: "EU (France, outermost region) — ePrivacy, GDPR",
  YT: "EU (France, outermost region) — ePrivacy, GDPR",
  MF: "EU (France, outermost region) — ePrivacy, GDPR",
  // United Kingdom: PECR reg. 6 (consent for analytics cookies; the DUA Act 2025 exemption is not yet relied on) + UK GDPR.
  GB: "UK — PECR reg. 6, UK GDPR",
  GI: "Gibraltar — Gibraltar GDPR and PECR-equivalent rules",
  // Switzerland: revFADP (nFADP, 2023) + FDPIC guidance; treated as opt-in to match EU practice.
  CH: "Switzerland — revFADP, FDPIC cookie guidance",
  // Canada: Quebec Law 25 (s. 8.1, tracking technologies off by default) and PIPEDA express-consent guidance.
  CA: "Canada — Quebec Law 25 s. 8.1, PIPEDA",
};

/** Cloudflare's placeholder codes: XX (unknown), T1 (Tor). */
const UNKNOWN_COUNTRIES = new Set(["XX", "T1"]);

/** "de" → "DE"; empty, malformed or placeholder codes → undefined (unknown). */
export function normalizeCountry(country: string | null | undefined): string | undefined {
  const code = (country ?? "").trim().toUpperCase();
  if (!/^[A-Z][A-Z0-9]$/.test(code) || UNKNOWN_COUNTRIES.has(code)) return undefined;
  return code;
}

/** The regional default before the person chooses. A privacy signal wins; an unknown country asks. */
export function analyticsDefault(country: string | null | undefined, signals: AnalyticsSignals = {}): AnalyticsDefault {
  if (signals.globalPrivacyControl === true || signals.doNotTrack === true) return "off";
  const code = normalizeCountry(country);
  if (code === undefined) return "ask";
  return Object.hasOwn(ASK_FIRST_COUNTRIES, code) ? "ask" : "on";
}

/**
 * What the app does: `collecting` (analytics runs), `prompt` (show the one-time question) and `setting` (how the
 * Settings switch reads: "on" chosen, "onByDefault" = "On (you can turn this off)", "off").
 */
export type AnalyticsStatus = { collecting: boolean; prompt: boolean; setting: "on" | "onByDefault" | "off" };

export function analyticsStatus(choice: AnalyticsChoice | null | undefined, fallback: AnalyticsDefault): AnalyticsStatus {
  if (choice === "granted") return { collecting: true, prompt: false, setting: "on" };
  if (choice === "denied") return { collecting: false, prompt: false, setting: "off" };
  if (fallback === "on") return { collecting: true, prompt: false, setting: "onByDefault" };
  return { collecting: false, prompt: fallback === "ask", setting: "off" };
}
